import React from 'react'
import { Card, Typography, Row, Col, Tag, Divider, message } from 'antd'
import { CheckCircleFilled } from '@ant-design/icons'
import { useTheme, AppTheme } from '../../common/ThemeContext'

const { Title, Text, Paragraph } = Typography

interface ThemeOption {
	key: AppTheme
	label: string
	tagline: string
	primaryColor: string
	accentColor: string
	bgColor: string
	headerBg: string
	description: string
	swatches: string[]
	logoText: string
}

const THEME_OPTIONS: ThemeOption[] = [
	{
		key: 'edc',
		label: 'EDC',
		tagline: 'Classic Corporate Blue',
		primaryColor: '#254a89',
		accentColor: '#ED1C24',
		bgColor: '#f0f2f5',
		headerBg: '#254a89',
		description:
			'Traditional EDC branding with deep navy blue as the primary color. Suited for a professional corporate environment.',
		swatches: ['#254a89', '#1d3a6e', '#ED1C24', '#f0f2f5', '#1C1B1A'],
		logoText: 'EDC',
	},
	{
		key: 'primersoft',
		label: 'Primersoft',
		tagline: 'Modern Charcoal with Red Accent',
		primaryColor: '#5b5f66',
		accentColor: '#ED1C24',
		bgColor: '#f3f4f6',
		headerBg: '#414042',
		description:
			'Official Primersoft identity with charcoal-led interface and red reserved for highlights and error emphasis.',
		swatches: ['#5b5f66', '#414042', '#ED1C24', '#f3f4f6', '#6b7280'],
		logoText: 'PS',
	},
	{
		key: 'dark',
		label: 'Dark Theme',
		tagline: 'Deep Midnight Sky',
		primaryColor: '#38bdf8',
		accentColor: '#fbbf24',
		bgColor: '#0f172a',
		headerBg: '#1e293b',
		description:
			'A high-contrast dark theme designed for low-light environments. Follows the midnight blue color palette.',
		swatches: ['#0f172a', '#1e293b', '#38bdf8', '#fbbf24', '#f8fafc'],
		logoText: 'DK',
	},
]

export default function ThemesTab() {
	const { theme, setTheme } = useTheme()

	const handleSelect = (key: AppTheme) => {
		if (key === theme) return
		setTheme(key)
		message.success(`Theme switched to "${key === 'edc' ? 'EDC' : key === 'primersoft' ? 'Primersoft' : 'Dark Theme'}"`, 2)
	}


	const CARD_STYLE = {
		borderRadius: 12,
		boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.03)',
	}

	return (
		<div>
			<Card style={{ ...CARD_STYLE, marginBottom: 24 }}>
				<Paragraph type='secondary' style={{ marginBottom: 0 }}>
					Choose a visual theme for the portal. The selected theme is saved to your browser and persists across sessions.
				</Paragraph>
			</Card>

			<Row gutter={[24, 24]}>
				{THEME_OPTIONS.map((opt) => {
					const isActive = theme === opt.key
					return (
						<Col key={opt.key} xs={24} sm={24} md={12}>
							<Card
								hoverable
								onClick={() => handleSelect(opt.key)}
								style={{
									border: isActive
										? `2px solid ${opt.primaryColor}`
										: '2px solid #f1f5f9',
									borderRadius: 12,
									cursor: 'pointer',
									transition: 'all 0.2s',
									boxShadow: isActive
										? `0 4px 18px ${opt.primaryColor}30`
										: '0 1px 2px 0 rgba(0, 0, 0, 0.03)',
									position: 'relative',
									overflow: 'hidden',
								}}
								styles={{ body: { padding: '24px' } }}
							>
								{/* Active badge */}
								{isActive && (
									<div
										style={{
											position: 'absolute',
											top: 12,
											right: 12,
											display: 'flex',
											alignItems: 'center',
											gap: 4,
											backgroundColor: opt.primaryColor,
											color: '#fff',
											borderRadius: 20,
											padding: '2px 10px',
											fontSize: 12,
											fontWeight: 600,
											zIndex: 2
										}}
									>
										<CheckCircleFilled style={{ fontSize: 12 }} />
										Active
									</div>
								)}

								{/* Mini header preview */}
								<div
									style={{
										height: 40,
										borderRadius: 8,
										backgroundColor: opt.headerBg,
										borderBottom:
											opt.key === 'primersoft'
												? `3px solid ${opt.primaryColor}`
												: 'none',
										display: 'flex',
										alignItems: 'center',
										paddingLeft: 12,
										gap: 8,
										marginBottom: 16,
									}}
								>
									{/* Mini logo dot */}
									<div
										style={{
											width: 20,
											height: 20,
											borderRadius: '50%',
											backgroundColor: '#fff',
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'center',
											fontSize: 8,
											fontWeight: 700,
											color: opt.primaryColor,
											flexShrink: 0,
										}}
									>
										{opt.logoText}
									</div>
									<div style={{ display: 'flex', gap: 6 }}>
										{[1, 2, 3].map((i) => (
											<div
												key={i}
												style={{
													width: 28,
													height: 8,
													borderRadius: 4,
													backgroundColor:
														i === 1
															? opt.primaryColor
															: 'rgba(255,255,255,0.3)',
												}}
											/>
										))}
									</div>
								</div>

								{/* Mini content preview */}
								<div
									style={{
										height: 52,
										borderRadius: 8,
										backgroundColor: '#f8fafc',
										padding: '8px 10px',
										marginBottom: 20,
										display: 'flex',
										gap: 8,
										border: '1px solid #f1f5f9'
									}}
								>
									{[1, 2, 3, 4].map((i) => (
										<div
											key={i}
											style={{
												flex: 1,
												borderRadius: 6,
												backgroundColor: '#fff',
												border: `1px solid #e2e8f0`,
												display: 'flex',
												alignItems: 'center',
												justifyContent: 'center',
											}}
										>
											<div
												style={{
													width: 14,
													height: 14,
													borderRadius: 3,
													backgroundColor:
														i % 2 === 0
															? `${opt.primaryColor}30`
															: `${opt.accentColor}20`,
												}}
											/>
										</div>
									))}
								</div>

								{/* Info */}
								<div style={{ marginBottom: 12 }}>
									<div
										style={{
											display: 'flex',
											alignItems: 'center',
											gap: 8,
											marginBottom: 8,
										}}
									>
										<Text strong style={{ fontSize: 17 }}>
											{opt.label}
										</Text>
									</div>
									<Text type='secondary' style={{ fontSize: 13, lineHeight: 1.5, display: 'block' }}>
										{opt.description}
									</Text>
								</div>

								{/* Color swatches */}
								<Divider style={{ margin: '16px 0' }} />
								<div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
									{opt.swatches.map((color) => (
										<div
											key={color}
											title={color}
											style={{
												width: 18,
												height: 18,
												borderRadius: '50%',
												backgroundColor: color,
												border: '1px solid rgba(0,0,0,0.05)',
												flexShrink: 0,
											}}
										/>
									))}
								</div>
							</Card>
						</Col>
					)
				})}
			</Row>

			<div
				style={{
					marginTop: 32,
					padding: '16px 20px',
					backgroundColor: '#f8fafc',
					borderRadius: 12,
					border: '1px solid #f1f5f9',
				}}
			>
				<Text type='secondary' style={{ fontSize: 13 }}>
					<strong>Tip:</strong> The theme applies globally to all pages. Refresh your browser if any part of the portal does not reflect the changes immediately.
				</Text>
			</div>
		</div>
	)
}
